<script lang="ts" setup>
import { computed, onBeforeUnmount } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  image: {
    type: String,
    default: '',
  },
  cancelLabel: {
    type: String,
    default: '',
  },
  confirmLabel: {
    type: String,
    default: '',
  },
  isButtonGrid: {
    type: Boolean,
    default: false,
  },
  hideCloseButton: {
    type: Boolean,
    default: false,
  },
  customClass: {
    type: String,
    default: '',
  },
  timeout: {
    type: Number,
    required: false,
    default: 0,
  },
})

const slots = useSlots()

const emit = defineEmits([
  'update:modelValue',
  'onCancel',
  'onConfirm',
  'onButtonClose',
])
const timeoutCloseSuccess = ref<NodeJS.Timeout | null>(null)

const visible = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

watch(
  () => props.modelValue,
  (value) => {
    if (value && props.timeout) {
      if (timeoutCloseSuccess.value) {
        clearTimeout(timeoutCloseSuccess.value)
      }
      timeoutCloseSuccess.value = setTimeout(
        () => (visible.value = false),
        props.timeout,
      )
    }
  },
)

onBeforeUnmount(() => {
  if (timeoutCloseSuccess.value) {
    clearTimeout(timeoutCloseSuccess.value)
  }
})

const onCancelClick = () => {
  emit('onCancel')
  visible.value = false
}

const onConfirmClick = () => {
  emit('onConfirm')
  visible.value = false
}

const onClose = () => {
  emit('onButtonClose')
  visible.value = false
}
</script>
<template>
  <BaseModal
    v-model="visible"
    :hide-close-button="props.hideCloseButton"
    class="base-confirm-modal"
    :class="customClass"
    @on-button-close="onClose"
  >
    <div class="base-confirm">
      <BaseImage
        v-if="image"
        class="base-confirm__image"
        :src="image"
        width="120"
        height="120"
      />
      <p v-if="title" class="base-confirm__title">{{ title }}</p>
      <!-- eslint-disable vue/no-v-html -->
      <div class="base-confirm__description">
        <template v-if="slots.description">
          <slot name="description" />
        </template>
        <template v-else>
          {{ props.description }}
        </template>
      </div>
    </div>
    <template #footer>
      <div v-if="slots.footer">
        <slot name="footer" />
      </div>
      <div v-else>
        <div class="base-confirm__footer">
          <BaseButton
            v-if="cancelLabel"
            :label="cancelLabel"
            variant="outline"
            :class="{ 'base-confirm__button-grid': isButtonGrid }"
            @click="onCancelClick"
          />
          <BaseButton
            v-if="confirmLabel"
            :label="confirmLabel"
            variant="primary"
            :class="{ 'base-confirm__button-grid': isButtonGrid }"
            @click="onConfirmClick"
          />
        </div>
      </div>
    </template>
  </BaseModal>
</template>
